import React from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import AppText from "../../components/Core/AppText/AppText";
import Button, { DownloadButton } from "../../components/Core/Button/Button";
import Flex from "../../components/Layout/Flex";
import Breadcrumbs from "../../components/Shared/Breadcrumbs/Breadcrumbs";
import InvoiceViewer from "../../components/Shared/InvoiceViewer/InvoiceViewer";
import { InvoiceDetailsBreadcrumbs } from "../../configs/breadcrumbNavigation";
import { ApiErrorResponse } from "../../models/ApiResponse";
import Invoice from "../../models/Invoice";
import { InvoiceExportType } from "../../models/Organisation";
import {
	useCreditInvoice,
	useDownloadInvoicePdf,
	useInvoice,
} from "../../networking/invoice.hooks";
import { useOrganisation } from "../../networking/organisation.hooks";
import { toast } from "../../utils/toast";

interface NavParams {
	invoiceId: string;
}

const InvoiceDetailsPage: React.FC = () => {
	const { t } = useTranslation();
	const { data: organisation } = useOrganisation();

	const { invoiceId } = useParams<keyof NavParams>() as NavParams;

	const { invoice } = useInvoice(invoiceId, ["customer"]);
	const { mutateAsync: creditInvoice } = useCreditInvoice();
	const { mutateAsync: downloadPdf } = useDownloadInvoicePdf();

	async function handleCreditInvoice(invoice: Invoice, exportType?: InvoiceExportType) {
		const confirmText = `${t("invoice.credit.confirm")}\n${
			exportType === "FORTNOX" ? t("invoice.credit.fortnoxInfo") : ""
		}`;

		if (window.confirm(confirmText)) {
			const toastId = toast.loading(t("invoice.credit.loading"));

			try {
				const data = await creditInvoice(invoice.id);
				toast.success(
					t("invoice.credit.success", {
						creditInvoiceNum: data?.creditInvoice.invoiceNumber,
					}),
					{ id: toastId }
				);
			} catch (e) {
				const error = (e as ApiErrorResponse)?.error;
				toast.error(t("invoice.credit.failed"), { id: toastId });

				if (error?.code === "INVOICE_NOT_BOOK_KEPT") {
					window.alert(t("invoice.credit.invoiceNotBookKept"));
				} else if (error?.code === "INVOICE_ALREADY_CREDITED") {
					window.alert(t("invoice.credit.invoiceAlreadyCredited"));
				}
			}
		}
	}

	/**
	 * Downloads invoice as PDF.
	 * So far only available for organisations using Fortnox.
	 * @param invoiceNum
	 */
	async function handleDownloadPDF(invoiceNum: string) {
		const toastId = toast.loading(t("invoice.downloading"));

		const data = await downloadPdf(invoiceNum).catch(() => undefined);

		if (data) {
			// https://stackoverflow.com/a/59352848
			const linkSource = `data:application/pdf;base64,${data.pdf}`;
			const downloadLink = document.createElement("a");
			const fileName = invoiceNum + ".pdf";
			downloadLink.href = linkSource;
			downloadLink.download = fileName;
			downloadLink.click();

			// The browser download is the feedback here, so the spinner just goes
			toast.dismiss(toastId);
		} else {
			toast.error(t("invoice.downloadFailed"), { id: toastId });
		}
	}

	const isFortnox = organisation?.invoiceSettings?.exportType === "FORTNOX";

	return (
		<>
			<Breadcrumbs items={InvoiceDetailsBreadcrumbs(invoice?.invoiceNumber)} />
			<div className="p-4 px-6">
				{isFortnox && invoice && (
					<Flex>
						<DownloadButton
							margin="0 1rem 0 0"
							onClick={() => handleDownloadPDF(invoice.invoiceNumber)}
						>
							{t("invoice.downloadPdf")}
						</DownloadButton>
						{!invoice.credit && !invoice.creditInvoiceReference && (
							<Button
								backgroundColor="danger"
								onClick={() =>
									handleCreditInvoice(invoice, organisation?.invoiceSettings?.exportType)
								}
							>
								{t("invoice.createCredit")}
							</Button>
						)}
					</Flex>
				)}
				<AppText size="l" weight="bold" margin="2rem 0 2rem 0">
					{t("invoice.invoice")} {invoice?.invoiceNumber}
				</AppText>
				<Flex direction="column" maxWidth="1000px">
					{invoice && <InvoiceViewer invoice={invoice} />}
				</Flex>
			</div>
		</>
	);
};

export default InvoiceDetailsPage;
